package com.karyadina.ui.navigation

import com.karyadina.model.AppFeature
import com.karyadina.service.PomodoroServiceImpl
import com.karyadina.ui.navigation.Screen
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.launch
import kotlinx.datetime.minus
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

// Abstract away ViewModel for multiplatform
// In a real MPP app, you'd use a library like KMM-ViewModel
open class MainViewModel(
    val pomodoroService: PomodoroServiceImpl,
    private val taskService: com.karyadina.service.TaskService? = null,
    private val storage: com.karyadina.service.KeyValueStorage? = null
) : ViewModel() {

    private val _uiState = MutableStateFlow(NavigationState())
    val uiState = _uiState.asStateFlow()
    
    private val _dashboardStats = MutableStateFlow(DashboardStats())
    val dashboardStats = _dashboardStats.asStateFlow()
    
    private val _preferences = MutableStateFlow(DashboardPreferences())
    val preferences = _preferences.asStateFlow()

    init {
        refreshDashboardStats()
        loadPreferences()
        
        // Observe storage refresh signals (e.g. from cloud sync)
        viewModelScope.launch {
            com.karyadina.service.AppStorage.refreshNotifier.collect {
                pomodoroService.refreshFromStorage()
                taskService?.refreshFromStorage()
                loadPreferences()
                refreshDashboardStats()
            }
        }
    }
    
    private fun loadPreferences() {
        storage?.let {
            val showDaily = it.loadBoolean("pref_show_daily_focus", true)
            val showWeekly = it.loadBoolean("pref_show_weekly_review", true)
            val startName = it.loadString("pref_user_persona")
            val personaName = startName ?: ""
            val persona = if (personaName.isNotEmpty()) {
                try { com.karyadina.model.UserPersona.valueOf(personaName) } catch (e: Exception) { null }
            } else null
            
            
            val showAll = it.loadBoolean("pref_show_all_tools", false)
            val alwaysShowDocs = it.loadBoolean("pref_always_show_doc_tools", false)
            
            val themeModeStr = it.loadString("pref_theme_mode")
            val themeMode = themeModeStr?.let { mode -> 
                try { com.karyadina.model.ThemeMode.valueOf(mode) } catch (e: Exception) { com.karyadina.model.ThemeMode.SYSTEM }
            } ?: com.karyadina.model.ThemeMode.SYSTEM
            
            val customThemeName = it.loadString("pref_custom_theme_enum")
            val customTheme = customThemeName?.let { name ->
                try { com.karyadina.model.AppThemeColor.valueOf(name) } catch (e: Exception) { null }
            }
            
            val language = it.loadString("app_language") ?: "en"
            
            // Restore locale
            com.karyadina.ui.util.PlatformLocale.changeLanguage(language)
            
            _preferences.value = DashboardPreferences(
                showDailyFocus = showDaily,
                showWeeklyReview = showWeekly,
                userPersona = persona,
                showAllTools = showAll,
                alwaysShowDocTools = alwaysShowDocs,
                themeMode = themeMode,
                customTheme = customTheme,
                language = language
            )
            
            // Check for first run
            if (persona == null) {
                // If no persona set, go to Onboarding
                 _uiState.update { it.copy(currentScreen = ScreenOnboarding) }
            }
        }
    }
    
    fun setUserPersona(persona: com.karyadina.model.UserPersona) {
        storage?.saveString("pref_user_persona", persona.name)
        _preferences.update { it.copy(userPersona = persona) }
        
        // After setting, navigate to Home
        onEvent(NavigationEvent.NavigateTo(ScreenHome))
    }
    
    fun toggleShowAllTools(show: Boolean) {
        storage?.saveBoolean("pref_show_all_tools", show)
        _preferences.update { it.copy(showAllTools = show) }
    }
    
    fun toggleAlwaysShowDocTools(show: Boolean) {
        storage?.saveBoolean("pref_always_show_doc_tools", show)
        _preferences.update { it.copy(alwaysShowDocTools = show) }
    }
    
    fun setThemeMode(mode: com.karyadina.model.ThemeMode) {
        storage?.saveString("pref_theme_mode", mode.name)
        _preferences.update { it.copy(themeMode = mode) }
    }
    
    fun setCustomTheme(theme: com.karyadina.model.AppThemeColor?) {
        if (theme != null) {
            storage?.saveString("pref_custom_theme_enum", theme.name)
        } else {
            storage?.remove("pref_custom_theme_enum")
        }
        _preferences.update { it.copy(customTheme = theme) }
    }
    
    fun setLanguage(code: String) {
        storage?.saveString("app_language", code)
        _preferences.update { it.copy(language = code) }
        com.karyadina.ui.util.PlatformLocale.changeLanguage(code)
    }


    
    fun resetPersona() {
        storage?.remove("pref_user_persona")
        storage?.remove("pref_show_all_tools")
        storage?.remove("pref_always_show_doc_tools")
        storage?.remove("pref_theme_mode")
        storage?.remove("pref_custom_theme_enum")
        _preferences.value = DashboardPreferences()
        
        // Navigate to Onboarding
        onEvent(NavigationEvent.NavigateTo(ScreenOnboarding))
    }
    
    fun toggleDailyFocus(show: Boolean) {
        storage?.saveBoolean("pref_show_daily_focus", show)
        _preferences.update { it.copy(showDailyFocus = show) }
    }
    
    fun toggleWeeklyReview(show: Boolean) {
        storage?.saveBoolean("pref_show_weekly_review", show)
        _preferences.update { it.copy(showWeeklyReview = show) }
    }

    fun onEvent(event: NavigationEvent) {
        when (event) {
            is NavigationEvent.NavigateTo -> {
                _uiState.update { it.copy(currentScreen = event.screen) }
                if (event.screen is ScreenHome) {
                    refreshDashboardStats()
                }
            }
            NavigationEvent.NavigateBack -> handleBackNavigation()
            is NavigationEvent.ToggleWeeklyReview -> {
                _uiState.update { it.copy(showWeeklyReview = event.show) }
            }
        }
    }
    
    private fun refreshDashboardStats() {
        taskService?.let { service ->
            val overdue = service.getOverdueTasks().size
            val today = service.getTodayTasks().size
            val upcoming = service.getUpcomingTasks().size
            _dashboardStats.value = DashboardStats(
                overdueCount = overdue,
                todayCount = today,
                upcomingCount = upcoming
            )
        }
    }
    
    private fun handleBackNavigation() {
        val current = _uiState.value.currentScreen
        val previousScreen = when (current) {
            is ScreenFeature, ScreenPomodoroHistory, ScreenOnboarding, ScreenAbout -> ScreenHome
            // Special case for history back navigation
            is ScreenPomodoroHistory -> ScreenFeature(AppFeature.POMODORO_TIMER)
            else -> ScreenHome // Default fall-back
        }
        _uiState.update { it.copy(currentScreen = previousScreen) }
        if (previousScreen is ScreenHome) {
            refreshDashboardStats()
        }
    }

    @OptIn(ExperimentalTime::class)
    fun getWeeklyReviewStats(): WeeklyReviewStats {
        if (taskService == null) return WeeklyReviewStats()
        
        val now = Clock.System.now()
        val oneWeekAgo = now.minus(kotlinx.datetime.DatePeriod(days = 7), kotlinx.datetime.TimeZone.currentSystemDefault())
        val timestamp = oneWeekAgo.toEpochMilliseconds()
        
        val completed = taskService.getTasksCompletedSince(timestamp)
        // Logic: review looks at what happened LAST week. 
        // Users want to see "what did I miss". That's overdue tasks.
        val overdue = taskService.getOverdueTasks() 
        
        return WeeklyReviewStats(
            completedTasks = completed,
            incompleteTasks = overdue,
            totalCompletedCount = completed.size
        )
    }
}

data class WeeklyReviewStats(
    val completedTasks: List<com.karyadina.model.Task> = emptyList(),
    val incompleteTasks: List<com.karyadina.model.Task> = emptyList(),
    val totalCompletedCount: Int = 0
)

data class NavigationState(
    val currentScreen: Screen = ScreenHome,
    val showWeeklyReview: Boolean = false
)

data class DashboardStats(
    val overdueCount: Int = 0,
    val todayCount: Int = 0,
    val upcomingCount: Int = 0
)

sealed interface NavigationEvent {
    data class NavigateTo(val screen: Screen) : NavigationEvent
    data object NavigateBack : NavigationEvent
    data class ToggleWeeklyReview(val show: Boolean) : NavigationEvent
}

data class DashboardPreferences(
    val showDailyFocus: Boolean = true,
    val showWeeklyReview: Boolean = true,
    val userPersona: com.karyadina.model.UserPersona? = null,
    val showAllTools: Boolean = false,
    val alwaysShowDocTools: Boolean = false,
    val themeMode: com.karyadina.model.ThemeMode = com.karyadina.model.ThemeMode.SYSTEM,
    val customTheme: com.karyadina.model.AppThemeColor? = null,
    val language: String = "en"
)